// Passing prepop params to a freshly spawned tool window. Short strings ride
// the URL as ?prepop=...; long ones (e.g. a trend set with hundreds of plots)
// blow past the dev server's ~16KB request-header limit and fail with
// 431 Request Header Fields Too Large, so they're stashed in localStorage
// (shared across same-origin windows) and the URL carries only ?prepopKey=.

const KEY_PREFIX = "tiger-prepop:";
export const INLINE_PREPOP_LIMIT = 2000;
// Entries are read by the spawned window moments after creation; anything
// older than this is an orphan (window failed to open) and gets purged.
const STALE_AFTER_MS = 10 * 60 * 1000;

function purgeStaleEntries(): void {
  const now = Date.now();
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (!key?.startsWith(KEY_PREFIX)) continue;
    const ts = Number(key.slice(KEY_PREFIX.length).split(":")[0]);
    if (!Number.isFinite(ts) || now - ts > STALE_AFTER_MS) {
      localStorage.removeItem(key);
    }
  }
}

/** Encode a prepop string as a URL query fragment ("prepop=..." or
 *  "prepopKey=..." for long payloads stashed in localStorage). */
export function encodePrepopParam(prepop: string): string {
  if (prepop.length <= INLINE_PREPOP_LIMIT) {
    return "prepop=" + encodeURIComponent(prepop);
  }
  purgeStaleEntries();
  const key = `${KEY_PREFIX}${Date.now()}:${Math.random().toString(36).slice(2)}`;
  localStorage.setItem(key, prepop);
  return "prepopKey=" + encodeURIComponent(key);
}

/** Read the current window's prepop string (inline ?prepop= or the
 *  localStorage entry named by ?prepopKey=). Safe to call repeatedly — the
 *  stored entry is left in place and reaped by purgeStaleEntries() later. */
export function readPrepopParam(): string | null {
  if (typeof window === "undefined") return null;
  const search = new URLSearchParams(window.location.search);
  const inline = search.get("prepop");
  if (inline) return inline;
  const key = search.get("prepopKey");
  return key ? localStorage.getItem(key) : null;
}
